import { Component, inject, ChangeDetectionStrategy, computed, signal, DestroyRef } from "@angular/core";
import { NOTIFICATION_STORE } from "../../stores";
import { IUserNotification } from "../../stores/notification/user-notification.model";
import { finalize, tap } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { ValueChangedEvent as TextBoxValueChangedEvent } from "devextreme/ui/text_box_types";

type NotificationFilter = 'All' | 'Unread';

@Component({
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.css']
})
export class NotificationsComponent {
  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #notificationStore = inject(NOTIFICATION_STORE);

  readonly pageSize = signal<number>(10);
  readonly pageIndex = signal<number>(1);
  readonly pageSizeOptions = [5, 10, 20, 50, 100];
  readonly filterOptions: NotificationFilter[] = ['All', 'Unread'];
  readonly selectedFilter = signal<NotificationFilter>('Unread');

  // Track selected notification IDs
  readonly selectedIds = signal<Set<string>>(new Set());

  // Separate signal for select all checkbox to prevent recursive triggers
  readonly selectAllChecked = computed<boolean>(() => {
    const allIds = this.notifications()
      .filter(notification => !notification.isRead)
      .map(notification => notification.id);
    const selected = this.selectedIds();
    return allIds.length > 0 && allIds.every(n => selected.has(n));
  });

  // Api call in progress
  readonly isUpdating = signal<boolean>(false); 

  readonly notifications = computed(() => {
    const start = (this.pageIndex() - 1) * this.pageSize();
    const end = start + this.pageSize();
    return this.filteredNotifications().slice(start, end);
  });

  readonly filteredNotifications = computed(() => {
    const term = this.searchTerm().toLowerCase();
    return this.#notificationStore.notifications()
      .filter(notification => this.selectedFilter() === 'All' || !notification.isRead)
      .filter(notification =>
        notification.taskName.toLowerCase().includes(term) ||
        notification.body.toLowerCase().includes(term) ||
        notification.sender.toLowerCase().includes(term)
      );
  });

  readonly totalNotifications = computed(() => this.filteredNotifications().length);  

  readonly isLoading = computed(() => this.#notificationStore.status() === 'loading');
  readonly initialising = computed(() => this.#notificationStore.status() === 'initialising');

  readonly searchTerm = signal<string>('');

  onPageSizeChange(event: number) {
    this.pageSize.set(event);
    this.resetPageAndSelection();
  }

  onPageIndexChange(event: number) {
    console.log('Page index changed:', event);
    this.pageIndex.set(event);
  }

  onItemSelectionChange(notificationId: string, event: any) {
    const newValue = event.value;
    const currentSelected = new Set(this.selectedIds());
    const notification = this.#notificationStore.notifications().find(item => item.id === notificationId);

    if (notification?.isRead) {
      currentSelected.delete(notificationId);
      this.selectedIds.set(currentSelected);
      return;
    }

    if (newValue) {
      currentSelected.add(notificationId);
    } else {
      currentSelected.delete(notificationId);
    }

    this.selectedIds.set(currentSelected);
  }

  isItemSelected(notificationId: string): boolean {
    return this.selectedIds().has(notificationId);
  }

  hasSelectedItems(): boolean {
    return this.selectedIds().size > 0;
  }

  selectedItems(): string[] {
    return Array.from(this.selectedIds());
  }

  markAsRead(ids: string[]) {
    const unreadIds = ids.filter(id =>
      this.#notificationStore.notifications().some(notification => notification.id === id && !notification.isRead)
    );

    if (unreadIds.length === 0) {
      return;
    }

    this.isUpdating.set(true);

    this.#notificationStore.markAsRead(unreadIds).pipe(
      tap(() => {
        const currentSelected = new Set(this.selectedIds());
        unreadIds.forEach(id => currentSelected.delete(id));
        this.selectedIds.set(currentSelected);

        const maximumPage = Math.max(1, Math.ceil(this.totalNotifications() / this.pageSize()));
        if (this.pageIndex() > maximumPage) {
          this.pageIndex.set(maximumPage);
        }
      }),
      finalize(() => this.isUpdating.set(false)),
      takeUntilDestroyed(this.#destroyRef)
    ).subscribe();
  }

  // Mention/comment notification bodies uniquely start with this prefix (see ItemCommentService.cs).
  isMentionNotification(notification: IUserNotification): boolean {
    return notification.body?.startsWith('New Message from') ?? false;
  }

  openLink(notification: IUserNotification) {
    const queryParams = this.isMentionNotification(notification)
      ? { taskId: notification.taskId, section: 'chat' }
      : { taskId: notification.taskId };
    this.#router.navigate(['/tasks'], { queryParams });
  }

  onSelectAllChange() {
    const action = this.selectAllChecked() ? 'deselect' : 'select';
    const allIds = this.notifications()
      .filter(notification => !notification.isRead)
      .map(notification => notification.id);

    switch (action) {
      case 'select':
        allIds.forEach(id => this.onItemSelectionChange(id, { value: true }));
        break;
      case 'deselect':
        allIds.forEach(id => this.onItemSelectionChange(id, { value: false }));
        break;
    }
  }

  onSearchTermChange(event: TextBoxValueChangedEvent) {
    this.searchTerm.set(event.value ?? '');
    this.resetPageAndSelection();
  }

  onFilterChange(event: { value?: NotificationFilter }) {
    if (!event.value || event.value === this.selectedFilter()) {
      return;
    }

    this.selectedFilter.set(event.value);
    this.resetPageAndSelection();
  }

  private resetPageAndSelection() {
    this.pageIndex.set(1);
    this.selectedIds.set(new Set());
  }
}